import { FormEvent, useEffect, useState } from 'react';
import {
  ProjectHealth,
  ProjectStage,
} from '../../services/api.service';

export interface ProjectEditorValues {
  name: string;
  client: string;
  owner: string;
  stage: ProjectStage;
  health: ProjectHealth;
  summary: string;
  nextAction: string;
}

interface ProjectEditorDialogProps {
  busy: boolean;
  errorMessage: string;
  mode: 'create' | 'edit';
  open: boolean;
  initialValues: ProjectEditorValues;
  onClose: () => void;
  onSubmit: (values: ProjectEditorValues) => Promise<void>;
}

const EMPTY_ERRORS = {
  client: '',
  name: '',
  owner: '',
};

export function createEmptyProjectDraft(): ProjectEditorValues {
  return {
    name: '',
    client: '',
    owner: '',
    stage: 'Awarded',
    health: 'On Track',
    summary: '',
    nextAction: '',
  };
}

export default function ProjectEditorDialog({
  busy,
  errorMessage,
  mode,
  open,
  initialValues,
  onClose,
  onSubmit,
}: ProjectEditorDialogProps) {
  const [draft, setDraft] = useState<ProjectEditorValues>(initialValues);
  const [fieldErrors, setFieldErrors] = useState(EMPTY_ERRORS);

  useEffect(() => {
    if (!open) {
      return;
    }

    setDraft(initialValues);
    setFieldErrors(EMPTY_ERRORS);
  }, [initialValues, open]);

  if (!open) {
    return null;
  }

  function updateField<K extends keyof ProjectEditorValues>(
    field: K,
    value: ProjectEditorValues[K]
  ) {
    setDraft((currentDraft) => ({ ...currentDraft, [field]: value }));

    if (field === 'name' || field === 'client' || field === 'owner') {
      setFieldErrors((currentErrors) => ({ ...currentErrors, [field]: '' }));
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const nextErrors = {
      client: draft.client.trim() ? '' : 'Client is required.',
      name: draft.name.trim() ? '' : 'Project name is required.',
      owner: draft.owner.trim() ? '' : 'Owner is required.',
    };

    setFieldErrors(nextErrors);

    if (nextErrors.name || nextErrors.client || nextErrors.owner) {
      return;
    }

    await onSubmit({
      ...draft,
      client: draft.client.trim(),
      name: draft.name.trim(),
      owner: draft.owner.trim(),
      nextAction: draft.nextAction.trim(),
      summary: draft.summary.trim(),
    });
  }

  return (
    <div className="project-dialog-backdrop" role="presentation" onClick={onClose}>
      <section
        aria-labelledby="project-editor-title"
        aria-modal="true"
        className="project-dialog"
        role="dialog"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="project-dialog__header">
          <div>
            <p className="project-dialog__eyebrow">
              {mode === 'create' ? 'New record' : 'Update record'}
            </p>
            <h2 id="project-editor-title">
              {mode === 'create' ? 'Create project' : 'Edit project'}
            </h2>
          </div>
          <button
            aria-label="Close dialog"
            className="ghost-button"
            type="button"
            onClick={onClose}
          >
            Close
          </button>
        </header>

        <form className="project-dialog__form" onSubmit={handleSubmit}>
          <label className="field">
            <span className="field__label">Project name</span>
            <input
              autoFocus
              type="text"
              value={draft.name}
              onChange={(event) => updateField('name', event.target.value)}
            />
            {fieldErrors.name ? (
              <span className="field__error">{fieldErrors.name}</span>
            ) : null}
          </label>

          <label className="field">
            <span className="field__label">Client</span>
            <input
              type="text"
              value={draft.client}
              onChange={(event) => updateField('client', event.target.value)}
            />
            {fieldErrors.client ? (
              <span className="field__error">{fieldErrors.client}</span>
            ) : null}
          </label>

          <label className="field">
            <span className="field__label">Owner</span>
            <input
              type="text"
              value={draft.owner}
              onChange={(event) => updateField('owner', event.target.value)}
            />
            {fieldErrors.owner ? (
              <span className="field__error">{fieldErrors.owner}</span>
            ) : null}
          </label>

          <label className="field field--full">
            <span className="field__label">Summary</span>
            <textarea
              rows={4}
              value={draft.summary}
              onChange={(event) => updateField('summary', event.target.value)}
            />
          </label>

          <label className="field field--full">
            <span className="field__label">Next action</span>
            <textarea
              rows={3}
              value={draft.nextAction}
              onChange={(event) => updateField('nextAction', event.target.value)}
            />
          </label>

          {errorMessage ? (
            <p className="form-state form-state--error">{errorMessage}</p>
          ) : null}

          <footer className="project-dialog__footer">
            <button className="ghost-button" type="button" onClick={onClose}>
              Cancel
            </button>
            <button className="primary-button" disabled={busy} type="submit">
              {busy
                ? mode === 'create'
                  ? 'Creating...'
                  : 'Saving...'
                : mode === 'create'
                  ? 'Create project'
                  : 'Save changes'}
            </button>
          </footer>
        </form>
      </section>
    </div>
  );
}
